import type {
  DashboardSourceControlDiscoveredRepository,
  DashboardSourceControlProviderDetailResponse,
  DashboardSourceControlRepository,
  DashboardSourceControlRepositoryUpsertRequest,
  DashboardSourceControlWebhook,
} from "../dashboard-client/types.gen";
import type {
  RepositoryIndexingRunFailure,
  SourceControlProviderDetail,
  SourceControlRepository,
} from "../types";

import {
  discoverRemoteRepositoriesAsUser,
  getFailedRepositoriesForIndexingRunAsUser,
  getSourceControlProviderDetailAsUser,
  listSourceControlRepositorySummariesAsUser,
  setupGithubAppInstallationAsUser,
  upsertSourceControlRepositoriesAsUser,
} from "../dashboard-client";
import { client } from "../dashboard-client/client.gen";

function normalizeRepository(
  repository: DashboardSourceControlRepository,
): SourceControlRepository {
  return { ...repository, topics: repository.topics ?? [] };
}

function normalizeProviderDetail(
  detail: DashboardSourceControlProviderDetailResponse,
): SourceControlProviderDetail {
  return {
    ...detail,
    repositories: detail.repositories.map(normalizeRepository),
    webhooks: detail.webhooks.filter(
      (webhook): webhook is Exclude<DashboardSourceControlWebhook, null> =>
        webhook !== null,
    ),
  };
}

export async function fetchSourceControlProviderDetail(
  providerId: string,
): Promise<SourceControlProviderDetail> {
  const { data } = await getSourceControlProviderDetailAsUser({
    path: { providerId },
  });
  return normalizeProviderDetail(data);
}

export async function fetchSourceControlRepositorySummary(): Promise<
  SourceControlRepository[]
> {
  const { data } = await listSourceControlRepositorySummariesAsUser();
  return data.repositories.map(normalizeRepository);
}

export interface StartGitHubAppInstallResult {
  redirect_url: string;
}

export async function setupGitHubAppInstallation(
  providerId: string,
  input?: { redirect_url?: string },
): Promise<StartGitHubAppInstallResult> {
  const { data } = await setupGithubAppInstallationAsUser({
    body: input ?? {},
    path: { providerId },
  });

  return data;
}

export type DiscoveredRepository = DashboardSourceControlDiscoveredRepository;

export async function discoverSourceControlRepositories(
  providerId: string,
): Promise<DiscoveredRepository[]> {
  const { data } = await discoverRemoteRepositoriesAsUser({
    path: { providerId },
  });
  return data.repositories;
}

export async function upsertSourceControlRepository(
  providerId: string,
  input: DashboardSourceControlRepositoryUpsertRequest,
): Promise<SourceControlRepository> {
  const { data } = await upsertSourceControlRepositoriesAsUser({
    body: input,
    path: { providerId },
  });

  const repository =
    data.repositories.find(
      (entry) => entry.repository_id === input.repository_id,
    ) ?? data.repositories.at(0);

  if (repository === undefined) {
    throw new Error("Repository upsert response did not include repository.");
  }

  return normalizeRepository(repository);
}

export async function deleteSourceControlRepository(
  providerId: string,
  repositoryId: string,
): Promise<void> {
  await client.delete({
    url: `/user/source-control/repositories/${encodeURIComponent(providerId)}/${encodeURIComponent(repositoryId)}`,
  });
}

export async function fetchRepositoryIndexingFailedRepositorySlugs(
  runId: string,
): Promise<string[]> {
  const { data } = await getFailedRepositoriesForIndexingRunAsUser({
    path: { runId },
  });
  return data.repositories;
}

export async function fetchRepositoryIndexingRunFailures(
  runId: string,
): Promise<RepositoryIndexingRunFailure[]> {
  const { data } = await getFailedRepositoriesForIndexingRunAsUser({
    path: { runId },
  });
  return data.failures;
}
